// Market Rate Engine — client-side library
// Handles lookups, AI analysis calls, and rate sheet persistence

import { supabase } from './supabase';

export const BASE_RATES = {
  remote_support:    165,
  onsite_block_2hr:  330,
  onsite_additional: 165,
  dev_crm:           220,
  design_ux:         140,
  pc_setup:          250,
};

export const RATE_LABELS = {
  remote_support:    'Remote Support / Help Desk',
  onsite_block_2hr:  'On-Site Dispatch (2hr Block)',
  onsite_additional: 'On-Site Additional Hourly',
  dev_crm:           'Dev / CRM / DB / Web',
  design_ux:         'Graphic Design & UX',
  pc_setup:          'PC Setup Fee',
};

export const RATE_UNITS = {
  remote_support:    '/hr',
  onsite_block_2hr:  ' block',
  onsite_additional: '/hr',
  dev_crm:           '/hr',
  design_ux:         '/hr',
  pc_setup:          '/ea',
};

const STALE_MONTHS = 3;

// ── Rate comparison rating ─────────────────────────────────────────────────
export function getRating(ferrumRate, marketRate) {
  if (!marketRate) return null;
  const diff = (ferrumRate - marketRate) / marketRate;
  if (diff > 0.25)       return { label: 'HIGH',          color: '#dc2626', bg: '#fef2f2', diff };
  if (diff > 0.10)       return { label: 'SLIGHTLY HIGH', color: '#d97706', bg: '#fffbeb', diff };
  if (diff < -0.05)      return { label: 'LOW',           color: '#2563eb', bg: '#eff6ff', diff };
  return                         { label: 'FAIR',          color: '#166534', bg: '#f0fdf4', diff };
}

// ── Check if analysis is stale ─────────────────────────────────────────────
export function isStale(analyzedAt) {
  if (!analyzedAt) return true;
  const cutoff = new Date();
  cutoff.setMonth(cutoff.getMonth() - STALE_MONTHS);
  return new Date(analyzedAt) < cutoff;
}

// ── Look up market by city+state (from zip lookup result) ──────────────────
export async function getMarketAnalysis(city, state) {
  if (!city || !state) return null;
  const { data } = await supabase
    .from('market_rate_analyses')
    .select('*')
    .ilike('city', city)
    .ilike('state', state)
    .single();
  return data || null;
}

// ── Run AI analysis for a market ───────────────────────────────────────────
export async function runMarketAnalysis(city, state, zip) {
  const res = await fetch('/.netlify/functions/marketAnalysis', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'analyze', payload: { city, state, zip, returnCityState: !city } })
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Analysis failed');
  return data.analysis;
}

// ── Save analysis to DB (upsert) ──────────────────────────────────────────
export async function saveMarketAnalysis(city, state, zip, analysis, source = 'ai_generated') {
  const cleanZip = zip ? String(zip).replace(/\D/g, '').slice(0, 5) : null;

  // First check if an existing record exists so we can merge zip_codes
  const { data: existing } = await supabase
    .from('market_rate_analyses')
    .select('id, zip_codes')
    .eq('city', city).eq('state', state)
    .maybeSingle();

  // Accumulate zip codes — merge existing array with new zip, no duplicates
  const existingZips = existing?.zip_codes || [];
  const mergedZips = cleanZip
    ? [...new Set([...existingZips, cleanZip])]
    : existingZips;

  const record = {
    city, state,
    zip: cleanZip || null,
    zip_codes: mergedZips,
    col_index:          analysis.col_index,
    median_income:      analysis.median_income || null,
    unemployment_rate:  analysis.unemployment_rate || null,
    market_tier:        analysis.market_tier,
    pricing_multiplier: analysis.pricing_multiplier,
    rates:              analysis.rates,
    market_notes:       analysis.market_notes || null,
    analysis_source:    source,
    analyzed_at:        new Date().toISOString(),
    updated_at:         new Date().toISOString(),
  };

  const { data, error } = await supabase
    .from('market_rate_analyses')
    .upsert(record, { onConflict: 'city,state' })
    .select()
    .single();

  if (error) throw new Error(error.message);
  return data;
}

// ── Look up by zip first ──────────────────────────────────────────────────
export async function getMarketByZip(zip) {
  if (!zip || zip.length < 5) return null;
  // Search both primary zip column AND zip_codes array so all associated ZIPs resolve.
  // v3.5.99.7.20: previously used .maybeSingle(), which ERRORS (and returns null)
  // when more than one row matches a ZIP. Duplicate/conflicting analyses for the
  // same ZIP (e.g. a stale wrong-city row carrying a ZIP that belongs to another
  // city) therefore made the lookup silently return null — surfacing to reps as
  // "No market analysis exists for this client's ZIP" even though one did exist,
  // and blocking the locked-rate display. Now we tolerate duplicates: take the
  // most recently analyzed row rather than failing the whole lookup.
  const { data } = await supabase
    .from('market_rate_analyses')
    .select('*')
    .or(`zip.eq.${zip},zip_codes.cs.{"${zip}"}`)
    .order('analyzed_at', { ascending: false })
    .limit(1);
  return data?.[0] || null;
}

// ── Get or analyze — main entry point ─────────────────────────────────────
// Accepts zip alone — resolves city/state via DB or AI
// Returns { analysis, wasRefreshed }
export async function getOrAnalyzeMarket(zip, forceRefresh = false, cityHint, stateHint, readOnly = false) {
  // Require either a zip or city+state — otherwise nothing to look up
  if (!zip && !(cityHint && stateHint)) return null;

  // 1. Try exact zip match first (only when zip is available)
  if (!forceRefresh && zip) {
    const byZip = await getMarketByZip(zip);
    if (byZip && !isStale(byZip.analyzed_at)) {
      return { analysis: byZip, wasRefreshed: false };
    }
  }

  // 2. Try city+state match if we have hints
  if (!forceRefresh && cityHint && stateHint) {
    const byCityState = await getMarketAnalysis(cityHint, stateHint);
    if (byCityState && !isStale(byCityState.analyzed_at)) {
      // Tag with zip for future lookups if we now have one
      if (zip && !byCityState.zip) {
        await supabase.from('market_rate_analyses')
          .update({ zip, updated_at: new Date().toISOString() })
          .eq('id', byCityState.id);
      }
      // Add this zip to the city's known zip_codes if not already there
      if (zip && !(byCityState.zip_codes || []).includes(zip)) {
        const updatedZips = [...new Set([...(byCityState.zip_codes || []), zip])];
        await supabase.from('market_rate_analyses')
          .update({ zip_codes: updatedZips, zip: byCityState.zip || zip, updated_at: new Date().toISOString() })
          .eq('id', byCityState.id);
        byCityState.zip_codes = updatedZips;
      }
      return { analysis: byCityState, wasRefreshed: false };
    }
  }

  // 3. readOnly mode — return stale data rather than triggering AI (used by quote pages on load)
  if (readOnly) {
    if (zip) { const byZip = await getMarketByZip(zip); if (byZip) return { analysis: byZip, wasRefreshed: false }; }
    if (cityHint && stateHint) { const byCityState = await getMarketAnalysis(cityHint, stateHint); if (byCityState) return { analysis: byCityState, wasRefreshed: false }; }
    return null;
  }

  // 4. Run AI analysis — pass zip + any hints (only when not readOnly)
  const aiResult = await runMarketAnalysis(cityHint || null, stateHint || null, zip || null);

  // AI returns city/state in the result
  const city  = aiResult.city  || cityHint  || 'Unknown';
  const state = aiResult.state || stateHint || 'XX';

  const saved = await saveMarketAnalysis(city, state, zip || null, aiResult, 'ai_generated');
  return { analysis: saved, wasRefreshed: true };
}

// ── Calculate after-hours rates from accepted on-site rate ─────────────────
export function calcAfterHoursRates(onsiteAdditional) {
  const base = onsiteAdditional || BASE_RATES.onsite_additional;
  const ahRate = Math.round(base * 1.5 / 5) * 5; // 1.5x, rounded to $5
  return {
    weekday_evening_rate:   ahRate,
    weekday_evening_dispatch: Math.round(300 * (base / 165) / 5) * 5,
    weekend_day_rate:       ahRate,
    weekend_day_dispatch:   Math.round(285 * (base / 165) / 5) * 5,
    graveyard_rate:         Math.round(ahRate * 1.15 / 5) * 5,
    graveyard_dispatch:     Math.round(380 * (base / 165) / 5) * 5,
  };
}

// ── Save accepted rate sheet to quote ─────────────────────────────────────
export async function saveRateSheet(quoteId, analysisId, marketCity, marketState, marketZip, acceptedRates, overrides, acceptedBy) {
  const { data, error } = await supabase
    .from('quote_rate_sheets')
    .upsert({
      quote_id:      quoteId,
      analysis_id:   analysisId,
      market_city:   marketCity,
      market_state:  marketState,
      market_zip:    marketZip,
      accepted_rates: acceptedRates,
      overrides:     overrides || {},
      accepted_by:   acceptedBy,
      accepted_at:   new Date().toISOString(),
    }, { onConflict: 'quote_id' })
    .select()
    .single();

  if (error) throw new Error(error.message);
  return data;
}

// ── Load existing rate sheet for a quote ──────────────────────────────────
export async function getRateSheet(quoteId) {
  if (!quoteId) return null;
  const { data } = await supabase
    .from('quote_rate_sheets')
    .select('*')
    .eq('quote_id', quoteId)
    .single();
  return data || null;
}

// ── Tier display helpers ──────────────────────────────────────────────────
// v3.5.65: tierColor extended to recognize the legacy static-tier keys
// (major_metro / mid_market / small_market / rural) emitted by the static
// marketTiers DB rows and by lookupZip() in pricing.js. Maps each legacy
// key to the same color used for its modern MI equivalent (per
// staticTierToMILabel). This lets QuotePage / BundleQuotePage / VoiceQuotePage
// drop their duplicate hardcoded mktColor / mktBg maps and call
// tierColor(t.tier_key) directly. Modern keys behave unchanged.
//
// The duplicate maps that lived in each quote page used a different blue
// for major_metro (#1e40af) than premium (#7c3aed) — which produced
// inconsistent badge colors depending on which page the rep was looking
// at. Centralizing here picks one canonical color per tier and applies it
// everywhere. Background helper added below for the same reason.
export function tierLabel(tier) {
  const map = { secondary: 'Secondary', adjusted: 'Adjusted', standard: 'Standard', premium: 'Premium' };
  return map[tier] || tier;
}

export function tierColor(tier) {
  const map = {
    // Modern MI vocabulary (rate-position)
    secondary: '#6b7280',
    adjusted:  '#d97706',
    standard:  '#2563eb',
    premium:   '#7c3aed',
    // Legacy static vocabulary (geographic) — map to the modern equivalent's color
    major_metro:  '#7c3aed',  // same as premium
    mid_market:   '#2563eb',  // same as standard
    small_market: '#d97706',  // same as adjusted
    rural:        '#6b7280',  // same as secondary
  };
  return map[tier] || '#374151';
}

// v3.5.65: tierBg — soft background companion to tierColor. Used by tier
// chips/badges/picker rows. Same legacy-aware mapping as tierColor.
export function tierBg(tier) {
  const map = {
    secondary: '#f3f4f6',
    adjusted:  '#fffbeb',
    standard:  '#dbeafe',
    premium:   '#ede9fe',
    major_metro:  '#ede9fe',
    mid_market:   '#dbeafe',
    small_market: '#fffbeb',
    rural:        '#f3f4f6',
  };
  return map[tier] || '#f3f4f6';
}

// ── v3.5.37: static-tier-to-MI-vocabulary translation ──────────────────────
// The static admin-managed marketTiers table uses geographic vocabulary
// (major_metro / mid_market / small_market / rural). The Market Intelligence
// engine uses rate-position vocabulary (premium / standard / adjusted /
// secondary). To unify what reps see, this helper translates static tier_keys
// to MI labels for display anywhere a tier name appears (dropdowns, badges,
// quote summaries, etc).
//
// Underlying data is unchanged in v3.5.37 — tier_key values stay as-is so
// existing quotes, snapshots, and SQL queries don't break. Only the displayed
// label changes. v3.5.38 may rename the underlying keys; this helper will
// continue to map old keys to new labels for backward compat.
export function staticTierToMILabel(tierKey) {
  const map = {
    major_metro:  'Premium',
    mid_market:   'Standard',
    small_market: 'Adjusted',
    rural:        'Secondary',
  };
  return map[tierKey] || tierKey;
}
